import type {
    TApiColumnMappingFieldType,
    TApiDealFileType,
    TApiFinalFileDestination,
    TApiSegment2MappingBehavior,
    TApiSegment4MappingBehavior,
    TApiSegment5MappingBehavior,
    TApiSegment6MappingBehavior,
    TApiSegment7MappingBehavior,
} from "@/apis/proda_finance";

export type TSegmentNumber = 2 | 4 | 5 | 6 | 7;

export type TSegmentConfig = {
    segmentNumber: TSegmentNumber;
    label: string;
    requiredColumns: string[];
    targetColumnName: string;
};

export const SEGMENT_CONFIGS: Record<TSegmentNumber, TSegmentConfig> = {
    2: {
        segmentNumber: 2,
        label: "Segment 2 Mappings",
        requiredColumns: ["Source Counterparty Code", "Source Counterparty Name", "Service Charge"],
        targetColumnName: "Service Charge",
    },
    4: {
        segmentNumber: 4,
        label: "Segment 4 Mappings",
        requiredColumns: [
            "Source Counterparty Code",
            "Source Counterparty Name",
            "Platform Property Code",
        ],
        targetColumnName: "Platform Property Code",
    },
    5: {
        segmentNumber: 5,
        label: "Segment 5 Mappings",
        requiredColumns: ["Source Counterparty Code", "Source Counterparty Name", "Movement"],
        targetColumnName: "Movement",
    },
    6: {
        segmentNumber: 6,
        label: "Segment 6 Mappings",
        requiredColumns: [
            "Source Counterparty Code",
            "Source Counterparty Name",
            "Analytical Dimension",
        ],
        targetColumnName: "Analytical Dimension",
    },
    7: {
        segmentNumber: 7,
        label: "Segment 7 Mappings",
        // Segment 7 (Deal) keeps the 3-column layout, but its Source Counterparty
        // Code column holds our Platform Property Code and is matched against the
        // line's property code during processing.
        requiredColumns: ["Source Counterparty Code", "Source Counterparty Name", "Deal"],
        targetColumnName: "Deal",
    },
};

export type TSegmentMappingRow = {
    source_counterparty_code: string;
    source_counterparty_name: string;
    target_value: string;
};

export type TSegmentMappingData = {
    file: File | null;
    rows: TSegmentMappingRow[];
    error: string | null;
    warnings: string[];
    isLoading: boolean;
};

export type TPropcodeMappingRow = {
    /** C, A, or P - derived from first character of Platform Entity Code */
    level: string;
    /** Third Entity Code */
    source_propcode: string;
    /** Platform Entity Code */
    target_propcode: string;
};

export type TPropcodeMappingData = {
    file: File | null;
    rows: TPropcodeMappingRow[];
    error: string | null;
    warnings: string[];
    isLoading: boolean;
    /** "copied" while the rows are the ones carried over from another deal (Copy deal). */
    origin?: "copied";
};

export const PROPCODE_REQUIRED_COLUMNS = ["Third Entity Code", "Platform Entity Code"];

export type TColumnMappingData = {
    file: File | null;
    headerRowNumber: number | "";
    columns: string[];
    mappings: Record<TApiColumnMappingFieldType, string>;
    error: string | null;
    warnings: string[];
    isLoading: boolean;
    /**
     * "copied" when the columns and mappings were carried over from another deal
     * (Copy deal) and no sample file is attached; absent for a normal upload.
     */
    origin?: "copied";
};

export const emptyColumnMappingData: TColumnMappingData = {
    file: null,
    headerRowNumber: 1,
    columns: [],
    mappings: {} as Record<TApiColumnMappingFieldType, string>,
    error: null,
    warnings: [],
    isLoading: false,
};

/** Backend requires a positive row; treat cleared field like the previous implicit default of 1 for extraction/submit. */
export function resolveHeaderRowNumberForApi(value: number | ""): number {
    if (value === "") return 1;
    return Math.max(1, value);
}

export const dealFileTypes: { value: TApiDealFileType; label: string }[] = [
    { value: "tb_and_gl", label: "TB and GL" },
    { value: "tb_only", label: "TB Only" },
];

export const finalDestinations: { value: TApiFinalFileDestination; label: string }[] = [
    { value: "yardi", label: "ERP" },
    { value: "data_platform", label: "Data Platform" },
];

export const segment2MappingTypes: { value: TApiSegment2MappingBehavior; label: string }[] = [
    { value: "no_mapping", label: "No Mapping" },
    { value: "source_code_mapping", label: "Source Code Mapping" },
];

export const segment4MappingTypes: { value: TApiSegment4MappingBehavior; label: string }[] = [
    { value: "no_mapping", label: "No Mapping" },
    { value: "source_inter_company_mapping", label: "Source Inter-Company Mapping" },
    { value: "sub_ledger", label: "Sub Ledger" },
];

export const segment5MappingTypes: { value: TApiSegment5MappingBehavior; label: string }[] = [
    { value: "no_mapping", label: "No Mapping" },
    { value: "source_account_mapping", label: "Source Account Mapping" },
];

export const segment6MappingTypes: { value: TApiSegment6MappingBehavior; label: string }[] = [
    { value: "no_mapping", label: "No Mapping" },
    { value: "source_account_mapping", label: "Source Account Mapping" },
];

export const segment7MappingTypes: { value: TApiSegment7MappingBehavior; label: string }[] = [
    { value: "no_mapping", label: "No Mapping" },
    { value: "source_account_mapping", label: "Property Code Mapping" },
];
